import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { appEnv } from '../../../../app.config';
import { SKIP_INTERCEPTOR } from '../../../interceptor/auth-interceptor';
import { JwtHelperService } from '@auth0/angular-jwt';
import { getInitials } from '../../../utils/string.utils';
import { finalize, Observable, shareReplay, tap } from 'rxjs';

export type Auth = {
  uuid: string,
  name: string,
  businessName: string,
  position: string,
  initialName: string,
  /** false for an added authorised representative (their token carries `rid`) */
  isPrimary: boolean,
  supplierId: number
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private api = `${appEnv.api}supplier-portal`;
  private jwtHelper = inject(JwtHelperService);
  private _auth = signal<Auth|null>(null);
  readonly authInfo = this._auth.asReadonly();
  private refreshToken$?: Observable<any>;

  constructor() { }

  generateOTL (email: string) {
    return this.http.post<{link?: string, message?: string}>(`${this.api}/auth/otl`, {email});
  }

  authenticate (code: string, uuid: string) {
    return this.http.post<any>(`${this.api}/auth/validate-otl`, {code, uuid}, {
      context: new HttpContext().set(SKIP_INTERCEPTOR, true)
    });
  }

  isValidToken () {
    return !this.jwtHelper.isTokenExpired(this.token())
  }

  private token () {
    return localStorage.getItem('supplier_token');
  }

  decodedToken (token: string) {
    return this.jwtHelper.decodeToken(token);
  }

  setAuth () {
    const token = localStorage.getItem('supplier_token');
    if (token) {
      const decodedToken = this.decodedToken(token);
      this._auth.set({
        uuid: decodedToken?.uuid ?? '',
        name: decodedToken?.name ?? '',
        businessName: decodedToken?.businessName ?? '',
        position: decodedToken?.position ?? 'Administrator',
        initialName: getInitials(decodedToken?.name),
        isPrimary: !decodedToken?.rid,
        supplierId: decodedToken.sub ?? 0
      });
    }
  }

  signOut () {
    return this.http.get<any>(`${this.api}/auth/sign-out`);
  }

  removeAuth () {
    localStorage.removeItem('supplier_token');
    localStorage.removeItem('supplier_rt');
  }

  getTokenExpirationDate () {
    return this.jwtHelper.getTokenExpirationDate(this.token() || '');
  }

  refreshToken () {
    if (this.refreshToken$) {
      return this.refreshToken$;
    }

    this.refreshToken$ = this.http.post<any>(`${this.api}/auth/refresh-token`, {
      refreshToken: localStorage.getItem('supplier_rt')
    }).pipe(
      tap(res => {
        if (res?.accessToken) {
          localStorage.setItem('supplier_token', res?.accessToken);
          this.setAuth();
        }
      }),
      shareReplay(1),
      finalize(() => this.refreshToken$ = undefined)
    );

    this.refreshToken$.subscribe({
      error: (err) => {
        console.error('Token refresh failed', err);
      }
    });
    return this.refreshToken$;
  }
}
